import React, { useCallback } from 'react';

import { ff } from '@atlassian/jira-feature-flagging';
import { useFlagsService } from '@atlassian/jira-flags';
import ErrorFlag from '@atlassian/jira-flags/src/common/ui/components/error-flag';
import SuccessFlag from '@atlassian/jira-flags/src/common/ui/components/success-flag';
import WarningFlag from '@atlassian/jira-flags/src/common/ui/components/warning-flag';
import { useIntl } from '@atlassian/jira-intl';
import { useInvitePeopleDrawer } from '@atlassian/jira-invite-people-drawer/src/controllers';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	type LinkData,
	getCoreInviteSubProduct,
	getProductDisplayName,
	getSiteAri,
} from '../../../utils';
import type { Invitees } from '../../types';
import { messages } from './messages';

const useOpenInvitePeopleModal = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();
	const addFlag = useCallback(
		// @ts-expect-error - TS7031 - Binding element 'appearance' implicitly has an 'any' type.
		({ appearance, ...flagProps }) => {
			if (appearance === 'success' || appearance === 'normal') {
				// @ts-expect-error Type error after Flag props have been fixed globally, to be fixed by owning team
				showFlag((serviceProps) => <SuccessFlag {...flagProps} {...serviceProps} />);
			}

			if (appearance === 'warning') {
				// @ts-expect-error Type error after Flag props have been fixed globally, to be fixed by owning team
				showFlag((serviceProps) => <WarningFlag {...flagProps} {...serviceProps} />);
			}

			if (appearance === 'error') {
				// @ts-expect-error Type error after Flag props have been fixed globally, to be fixed by owning team
				showFlag((serviceProps) => <ErrorFlag {...flagProps} {...serviceProps} />);
			}
		},
		[showFlag],
	);
	const [, { openDrawer }] = useInvitePeopleDrawer();

	return useCallback(
		(insertedLink: string, { name, product, tenantId }: LinkData, invitees: Invitees) => {
			openDrawer(
				createAnalyticsEvent ?? null,
				{
					inviteFlow: 'inviteLinkInsert',
					analyticsSource: 'inviteLinkInsert',
				},
				undefined,
				{
					continueUrl: insertedLink,
					experimental_formTitleByline: name
						? formatMessage(messages.coreInvitesTitleByline, {
								title: name,
							})
						: undefined,
					experimental_userPickerByline: formatMessage(messages.coreInvitesUserPickerByline, {
						productDisplayName: getProductDisplayName(product),
					}),
					experimentalFeatures: {
						invitePromptOnLinkV3: true,
						inviteThirdPartyTeams: 'variation',
					},
					initialInvitees: invitees,
					showFlag: addFlag,
					source: 'inviteLinkInsert',
					subProduct: getCoreInviteSubProduct(product),
					resourceAri: getSiteAri(tenantId, product),
					maxNumberOfInputs: ff('aioli-v3-allow-30-users-from-core-invites', false) ? 30 : 10,
				},
			);
		},
		[addFlag, createAnalyticsEvent, formatMessage, openDrawer],
	);
};

export default useOpenInvitePeopleModal;
